<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('returnToVendor.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('returnToVendor.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-flex ts-items-center ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <a-range-picker
              v-model="dateRange"
              :allowClear="false"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              style="width: 200px"
              @change="fetchData()"
            />
          </div>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <ts-input
              v-model="search"
              v-tooltip="$t('returnToVendor.search')"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <ts-table
          :columns="columns"
          :records="resources"
          :loading="loading"
          :expandable="true"
        >
          <template v-slot="{ record }">
            <td class="ts-whitespace-no-wrap">{{ record.return_date }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.return_number }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.vendor_name }}</td>
            <td class="ts-whitespace-no-wrap">{{ record.status }}</td>
            <td class="ts-whitespace-no-wrap ts-text-center">
              <a-switch
                v-if="record.is_refund"
                v-model="record.is_refund_received"
                size="small"
                :loading="record.loading_receive_refund"
                @change="onRefundReceive(record)"
              />
            </td>
            <td class="ts-whitespace-no-wrap">{{ record.remarks }}</td>
            <td class="text-center ts-space-x-3">
              <a
                href="#"
                v-tooltip="$t('paymentToVendor.reprint')"
                class="text-primary text-red"
                @click.prevent="reprintReport(record)"
              >
                <i class="fas fa-file-pdf"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary"
                @click.prevent="edit(record)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(record)"
              >
                <a href="#" :disabled="record._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="record._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
            </td>
          </template>
          <div slot="expand" slot-scope="prop">
            <table class="ts-w-11/12 ts-mx-auto">
              <thead>
                <tr>
                  <th>{{ $t('returnToVendor.productGroup') }}</th>
                  <th>{{ $t('returnToVendor.productName') }}</th>
                  <th>{{ $t('returnToVendor.productType') }}</th>
                  <th>{{ $t('returnToVendor.uom') }}</th>
                  <th>{{ $t('returnToVendor.cost') }}</th>
                  <th>{{ $t('returnToVendor.quantity') }}</th>
                  <th class="ts-w-8">
                    {{ $t('returnToVendor.quantityReceive') }}
                  </th>
                  <th class="ts-text-center">
                    {{ $t('returnToVendor.piNumber') }}
                  </th>
                  <th class="ts-text-center">
                    {{ $t('returnToVendor.piDate') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(product, index) in prop.record.details">
                  <tr :key="index">
                    <td class="ts-font-bold ts-text-blue-600">
                      {{ product.pro_group_name_en }}
                    </td>
                    <td class="ts-font-bold ts-text-blue-600">
                      {{ product.product_name_en }}
                    </td>
                    <td class="ts-font-bold ts-text-blue-600">
                      {{ product.product_type_en }}
                    </td>
                    <td class="ts-font-bold ts-text-blue-600">
                      {{ product.uom_name_en }}
                    </td>
                    <td class="ts-font-bold ts-text-blue-600">
                      {{ $defaultCurrencyWithCode(product.cost) }}
                    </td>
                    <td class="ts-font-bold ts-text-blue-600">
                      {{ product.quantity }}
                    </td>
                    <td class="ts-font-bold ts-text-center ts-text-red-600">
                      {{ product.quantity_receive }}
                    </td>
                    <td class="ts-font-bold ts-text-center ts-text-blue-600">
                      {{ product.stock_in_number }}
                    </td>
                    <td class="ts-font-bold ts-text-center ts-text-blue-600">
                      {{ product.stock_in_date }}
                    </td>
                  </tr>
                  <tr v-if="product.serials.length">
                    <td colspan="9">
                      <div class="ts-pl-3 ts-space-y-2 ts-space-x-1">
                        <div
                          class="ts-border-blue-200 ts-border ts-p-2 ts-rounded ts-font-bold w-fit ts-inline-block"
                          v-for="serial in product.serials"
                          :key="serial.serial_no"
                        >
                          {{ serial.serial_no }}
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </ts-table>
      </div>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
      <a-modal :title="$t('pdf')" width="50%" v-model="show_pdf" :footer="null">
        <ts-preview-pdf v-model="waiting" :src="src" />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapActions, mapState } from 'vuex'
import moment from 'moment'
export default {
  name: 'stockReturnToVendor',
  data () {
    return {
      loading: false,
      show_pdf: false,
      waiting: false,
      src: ''
    }
  },
  created () {
    this.$store.state.stock.returnToVendor.dateRange = [
      moment().add(-1, 'months').format('DD-MM-YYYY'),
      moment().format('DD-MM-YYYY')
    ]
  },
  computed: {
    ...mapState('stock/returnToVendor', ['resources', 'pagination']),
    search: {
      get () {
        return this.$store.state.stock.returnToVendor.search
      },
      set (newValue) {
        this.$store.commit('stock/returnToVendor/SET_SEARCH', newValue)
        this.$store.commit('stock/returnToVendor/RESET_CURRENT_PAGE')
      }
    },
    dateRange: {
      get () {
        return this.$store.state.stock.returnToVendor.dateRange
      },
      set (newValue) {
        this.$store.commit('stock/returnToVendor/SET_DATE_RANGE', newValue)
      }
    },
    columns () {
      return [
        { name: this.$t('returnToVendor.returnDate') },
        { name: this.$t('returnToVendor.returnNumber') },
        { name: this.$t('returnToVendor.vendor') },
        { name: this.$t('returnToVendor.status') },
        {
          name: this.$t('returnToVendor.refundReceived'),
          style: 'text-align:center'
        },
        { name: this.$t('returnToVendor.remark') },
        {
          name: this.$t('actions'),
          style: 'text-align:center; width:7%'
        }
      ]
    }
  },
  methods: {
    ...mapActions('stock/returnToVendor', ['setRefundReceived']),
    addNew () {
      this.$router.push({ name: 'return-to-vendor-create' })
    },
    fetchData (attributes) {
      this.loading = true
      this.$store
        .dispatch('stock/returnToVendor/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit (record) {
      this.$store.commit('stock/returnToVendor/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'return-to-vendor-edit',
        params: { id: record.return_to_vendor_id }
      })
    },
    deleteRecord (record) {
      record._deleting = true
      this.$store
        .dispatch('stock/returnToVendor/destroy', record.return_to_vendor_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    onRefundReceive (record) {
      record.loading_receive_refund = true
      this.setRefundReceived(record.return_to_vendor_id)
        .then(response => {
          record.is_refund_received = response.is_refund_received
        })
        .catch(error => {
          record.is_refund_received = !record.is_refund_received
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record.loading_receive_refund = false
        })
    },
    reprintReport (record) {
      this.show_pdf = true
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('stock/returnToVendor/toPdf', record.return_to_vendor_id)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.waiting = false
        })
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  },
  beforeRouteLeave (to, from, next) {
    this.$store.commit('stock/returnToVendor/RESET_STATE')
    next()
  }
}
</script>
